import { useState, useEffect } from 'react'
import { api, type Task, type TeamMember } from '../lib/api'
import { hapticFeedback, hapticNotification } from '../lib/telegram'
import { SubtasksList } from './SubtasksList'

interface TasksViewV2Props {
  category: 'work' | 'personal'
  onRefresh: () => void
}

export function TasksViewV2({ category, onRefresh }: TasksViewV2Props) {
  const [tasks, setTasks] = useState<Task[]>([])
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'urgent' | 'assigned'>('all')
  const [selectedMember, setSelectedMember] = useState<string>('')
  const [expandedTask, setExpandedTask] = useState<string | null>(null)
  const [taskSubtasks, setTaskSubtasks] = useState<Record<string, any[]>>({})

  const fetchData = async () => {
    try {
      setLoading(true)
      const [tasksData, teamData] = await Promise.all([
        api.getTasks({ status: 'open', category }),
        api.getTeamMembers(),
      ])
      setTasks(tasksData)
      setTeamMembers(teamData)
    } catch (e) {
      console.error('Failed to fetch tasks:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [category])

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'border-red-500'
      case 'this_week': return 'border-yellow-500'
      case 'normal': return 'border-blue-500'
      case 'backlog': return 'border-gray-500'
      default: return 'border-gray-500'
    }
  }

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return '🔴'
      case 'this_week': return '🟡'
      case 'normal': return '🔵'
      case 'backlog': return '⚪'
      default: return '⚪'
    }
  }

  const getTimeEstimateDisplay = (timeEstimate: string | null) => {
    if (!timeEstimate) return null
    const estimates: Record<string, string> = {
      '15min': '15m',
      '1hr': '1h',
      'half_day': '4h',
      'full_day': '8h'
    }
    return estimates[timeEstimate] || timeEstimate
  }

  const getDaysLeft = (dueDate: string | null) => {
    if (!dueDate) return null
    const due = new Date(dueDate)
    const now = new Date()
    const diffTime = due.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) return 'Overdue'
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return '1d'
    if (diffDays <= 7) return `${diffDays}d`
    return `${Math.ceil(diffDays / 7)}w`
  }

  const handleCompleteTask = async (taskId: string) => {
    try {
      hapticNotification('success')
      await api.updateTask(taskId, { completed: true })
      await fetchData()
      onRefresh()
    } catch (e) {
      hapticNotification('error')
      console.error('Failed to complete task:', e)
    }
  }

  const handleQuickAssign = async (taskId: string, memberName: string) => {
    try {
      hapticFeedback('light')
      await api.updateTask(taskId, { assigned_to: memberName })
      await fetchData()
    } catch (e) {
      console.error('Failed to assign task:', e)
    }
  }

  const handleExpandTask = async (taskId: string) => {
    if (expandedTask === taskId) {
      setExpandedTask(null)
      return
    }
    
    hapticFeedback('light')
    setExpandedTask(taskId)
    
    // Fetch subtasks if not already loaded
    if (!taskSubtasks[taskId]) {
      try {
        const subtasks = await api.getSubtasks(taskId)
        setTaskSubtasks(prev => ({ ...prev, [taskId]: subtasks }))
      } catch (e) {
        console.error('Failed to fetch subtasks:', e)
      }
    }
  }

  const handleSubtaskUpdate = async (taskId: string) => {
    try {
      const subtasks = await api.getSubtasks(taskId)
      setTaskSubtasks(prev => ({ ...prev, [taskId]: subtasks }))
      await fetchData() // Refresh tasks to update progress counts
    } catch (e) {
      console.error('Failed to refresh subtasks:', e)
    }
  }

  const filteredTasks = tasks.filter(task => {
    if (filter === 'urgent') return task.priority === 'urgent'
    if (filter === 'assigned') return task.assigned_to
    return true
  }).filter(task => {
    if (selectedMember) return task.assigned_to === selectedMember
    return true
  })

  const getTeamMemberEmoji = (memberName: string | null) => {
    if (!memberName) return null
    const member = teamMembers.find(m => m.name === memberName)
    return member?.emoji || '👤'
  }

  if (loading) {
    return (
      <div className="px-4 py-8 text-center">
        <div className="spinner" />
        <p className="text-sm text-muted mt-3">Loading tasks...</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold flex items-center gap-2">
            {category === 'work' ? '💼' : '🏠'} {category.charAt(0).toUpperCase() + category.slice(1)} Tasks
          </h1>
          <span className="text-sm text-muted">{filteredTasks.length} tasks</span>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
          {[
            { id: 'all', label: 'All', icon: '📋' },
            { id: 'urgent', label: 'Urgent', icon: '🔥' },
            { id: 'assigned', label: 'Assigned', icon: '👥' },
          ].map(({ id, label, icon }) => (
            <button
              key={id}
              onClick={() => {
                setFilter(id as any)
                hapticFeedback('light')
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                filter === id
                  ? 'bg-accent-light/20 text-accent-light'
                  : 'bg-white/5 text-muted hover:bg-white/10'
              }`}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        {/* Team filter */}
        {category === 'work' && teamMembers.length > 0 && (
          <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => {
                setSelectedMember('')
                hapticFeedback('light')
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                !selectedMember
                  ? 'bg-blue-500/20 text-blue-300'
                  : 'bg-white/5 text-muted hover:bg-white/10'
              }`}
            >
              👤 All
            </button>
            {teamMembers.map((member) => (
              <button
                key={member.id}
                onClick={() => {
                  setSelectedMember(member.name)
                  hapticFeedback('light')
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  selectedMember === member.name
                    ? 'bg-blue-500/20 text-blue-300'
                    : 'bg-white/5 text-muted hover:bg-white/10'
                }`}
              >
                {member.emoji} {member.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tasks */}
      <div className="px-4">
        {filteredTasks.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">🎉</div>
            <p className="text-muted">No tasks found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                className={`
                  glass rounded-xl p-4 border-l-4 ${getPriorityColor(task.priority)}
                  transition-all
                  ${expandedTask === task.id ? 'ring-2 ring-accent-light/30' : 'active:scale-[0.98]'}
                `}
              >
                {/* Header */}
                <div className="flex items-start gap-3 mb-2">
                  <button
                    onClick={() => handleCompleteTask(task.id)}
                    className="w-5 h-5 rounded border-2 border-white/30 flex items-center justify-center mt-0.5 active:scale-90 transition-all hover:border-green-400"
                  >
                    <div className="w-2 h-2 rounded-full bg-transparent" />
                  </button>
                  <div className="flex-1">
                    <button
                      onClick={() => handleExpandTask(task.id)}
                      className="w-full text-left"
                    >
                      <h3 className="font-medium text-white/90 leading-tight hover:text-white transition-colors">
                        {task.title}
                      </h3>
                      
                      {/* Subtask progress */}
                      {(task.subtask_total ?? 0) > 0 && (
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden max-w-[120px]">
                            <div 
                              className="h-full bg-gradient-to-r from-green-400 to-blue-500 transition-all duration-300"
                              style={{ 
                                width: `${((task.subtask_completed ?? 0) / (task.subtask_total ?? 1)) * 100}%` 
                              }}
                            />
                          </div>
                          <span className="text-xs text-muted">
                            {task.subtask_completed}/{task.subtask_total} steps
                          </span>
                        </div>
                      )}
                    </button>
                  </div>
                  <span className="text-sm">{getPriorityIcon(task.priority)}</span>
                </div>

                {/* Meta info */}
                <div className="ml-8 space-y-2">
                  {task.project_name && (
                    <p className="text-xs text-muted">
                      {task.project_emoji} {task.project_name}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    {task.time_estimate && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-white/70">
                        ⏱️ {getTimeEstimateDisplay(task.time_estimate)}
                      </span>
                    )}
                    
                    {task.due_date && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        getDaysLeft(task.due_date) === 'Overdue' ? 'bg-red-500/20 text-red-300' :
                        getDaysLeft(task.due_date) === 'Today' ? 'bg-yellow-500/20 text-yellow-300' :
                        'bg-blue-500/10 text-blue-300'
                      }`}>
                        📅 {getDaysLeft(task.due_date)}
                      </span>
                    )}
                    
                    {task.assigned_to && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/10 text-purple-300">
                        {getTeamMemberEmoji(task.assigned_to)} {task.assigned_to}
                      </span>
                    )}
                    
                    {!task.assigned_to && category === 'work' && (
                      <div className="relative group">
                        <button className="px-2 py-0.5 rounded-full text-xs bg-white/5 text-white/50 hover:bg-white/10">
                          👤 Assign
                        </button>
                        <div className="absolute top-full left-0 mt-1 glass rounded-lg shadow-xl z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                          <div className="p-2 space-y-1 min-w-[120px]">
                            {teamMembers.map((member) => (
                              <button
                                key={member.id}
                                onClick={() => handleQuickAssign(task.id, member.name)}
                                className="w-full text-left px-2 py-1 text-xs rounded hover:bg-white/10 flex items-center gap-2"
                              >
                                {member.emoji} {member.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded subtasks */}
                {expandedTask === task.id && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-white/80">Task Breakdown</h4>
                      <button
                        onClick={() => setExpandedTask(null)}
                        className="text-xs text-muted hover:text-white/80 transition-colors"
                      >
                        Collapse
                      </button>
                    </div>
                    
                    {taskSubtasks[task.id] ? (
                      <SubtasksList
                        taskId={task.id}
                        subtasks={taskSubtasks[task.id]}
                        onSubtaskUpdate={() => handleSubtaskUpdate(task.id)}
                        showProgress={false} // Already shown above
                        allowAdd={true}
                      />
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-sm text-muted">Loading subtasks...</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}